import { ReactNode } from "react";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/shared/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { cn } from "@/shared/lib/utils";
import { useAuth } from "@/features/auth/model/AuthContext";
import { Avatar, AvatarFallback } from "@/shared/ui/avatar";
import { User, LogOut, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Button } from "@/shared/ui/button";
import { Footer } from "./Footer";
import { LanguageSelector } from "@/shared/widgets/language-selector";

interface AppLayoutProps {
  children: ReactNode;
  className?: string;
}

export function AppLayout({ children, className }: AppLayoutProps) {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen min-h-0 bg-[#1A1A1A] text-white w-full">
        <AppSidebar />
        <SidebarInset className="flex min-h-0 flex-1 flex-col">
          <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 px-4 bg-[#1A1A1A]">
            <SidebarTrigger className="text-white hover:bg-white/10 hover:text-white" />

            <div className="flex items-center gap-2">
              {/* Seletor de idioma */}
              <LanguageSelector />
              
              {/* Dropdown do usuário logado */}
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 h-auto p-2 hover:bg-white/10"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[#D91657] text-white text-sm">
                      {
                        user?.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.name || user.username || user.email}
                            className="h-8 w-8"
                          />
                        ) : (
                          <User className="h-4 w-4" />
                        )
                      }
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium text-white">
                      {user?.name || user?.username || user?.email}
                    </span>
                    <span className="text-xs text-white/50">
                      {user?.role}
                    </span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-white/60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56 bg-[#222222] border-white/10"
              >

                <DropdownMenuLabel className="text-white">
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.email}</span>
                    <span className="text-xs text-white/60">{user?.role}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
          <main className={cn("flex min-h-0 flex-1 flex-col overflow-auto p-6 bg-[#1A1A1A] min-w-0", className)}>
            {children}
            <Footer />
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}